import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Crown, Mail } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import type { User } from '@/types';

interface MemberPlanInfoCardProps {
  owner: User['effectiveOwner'];
}

/**
 * MemberPlanInfoCard — readonly plan info card for `invited_member` users.
 *
 * v3.4 (25/05/26) — when an invited member visits SubscriptionTab or
 * CompanyPlanTab we render this card instead of the regular plan management
 * UI. It tells the user the plan is managed by the owner and surfaces the
 * owner's email as a mailto link so the member can request changes.
 */
export function MemberPlanInfoCard({ owner }: MemberPlanInfoCardProps) {
  const { t } = useLanguage();
  const ownerName =
    owner?.displayName || owner?.email || t('profile.header.individual');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5 text-muted-foreground" />
          {t('subscription.member.cardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border bg-muted/30 p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Crown className="h-4 w-4 text-brand-accent" />
            {t('subscription.member.managedByOwner', { owner: ownerName })}
          </div>
          <p className="text-sm text-muted-foreground">
            {t('subscription.member.contactToChange')}
          </p>
        </div>

        {owner?.email && (
          <div className="rounded-md border border-border bg-background p-3 flex items-center gap-3">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">
                {t('subscription.member.contactEmail')}
              </p>
              <a
                href={`mailto:${owner.email}`}
                className="font-mono text-sm text-primary hover:underline truncate block"
              >
                {owner.email}
              </a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
